import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal"; // Import react-modal
import "../../index.css";
import { MaterialReactTable } from "material-react-table";
import Button from "@mui/material/Button";
import loadingGif from "../../loading.gif";
import loadingOrderGif from "../../loadingOrder.gif";
import ProductInfoModal from "../ProductInfoModal/ProductInfoModal";
import AlertModal from "../AlertModal/AlertModal";
import { useTheme, getCssVar } from "../../hooks/useTheme";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "min(720px, 90vw)",
    maxHeight: "85vh",
    overflowY: "auto",
  },
};

function Dashboard({ setOrderSummary, orderSummary, modAuthFalse }) {
  const [allData, setAllData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [modalOrderIsOpen, setModalOrderIsOpen] = useState(false);

  const [confirmOrderLoader, setConfirmOrderLoader] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalIsOpen, setProductModalIsOpen] = useState(false);
  const [alert, setAlert] = useState(null);

  const theme = useTheme();
  const palette = useMemo(() => ({
    text: getCssVar("--fa-text", theme === "light" ? "#0a0e0a" : "#e5ede5"),
    muted: getCssVar("--fa-muted", theme === "light" ? "#5a6a5a" : "#9aa89a"),
    surface: getCssVar("--fa-surface", theme === "light" ? "#ffffff" : "#0e130e"),
    border: getCssVar("--fa-border", theme === "light" ? "#cfd8cf" : "#2a3a2a"),
    accent: getCssVar("--fa-accent", "#2e7d32"),
  }), [theme]);
  const isLight = theme === "light";

  const handleConfirmOrder = async () => {
    setConfirmOrderLoader(true); // Display loader
    closeModal();
    const token = localStorage.getItem('token'); // Get JWT token from localStorage
    console.log({ token });
    try {
      const response = await fetch("https://api.fullagro.com:8888/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Add token to authorization header
        },
        body: JSON.stringify({
          orders: orderSummary, // Send order summary
        }),
      });

      if (response.ok) {
        setAlert({ variant: "success", title: "Pedido confirmado", message: "Hemos creado tu pedido. Recibirás una copia por email." });
        setOrderSummary([]);
        setRowSelection({});
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error creating orders:", error.message);
      if (error.message.includes("401")) {
        setAlert({ variant: "error", title: "Sesión expirada", message: "Tu sesión ya no es válida. Por favor, vuelve a iniciar sesión." });
      } else {
        setAlert({ variant: "error", title: "Error al procesar", message: "No pudimos crear el pedido. Inténtalo de nuevo en unos segundos." });
      }
    } finally {
      setConfirmOrderLoader(false); // Hide the loader regardless of the result
    }
  };

  

  const closeModal = () => {
    setModalOrderIsOpen(false);
  };

  const openProductModal = (product) => {
    setSelectedProduct({ ...product, quantity: product.quantity || 0 });
    setProductModalIsOpen(true);
  };

  const closeProductModal = () => {
    setProductModalIsOpen(false);
    setSelectedProduct(null);
  };

  const handleSelectBatch = (batch) => {
    setSelectedProduct({ ...batch, quantity: 0 });
  };

  const handleAddToOrder = (product) => {
    // Verificar si el producto ya existe en orderSummary
    const existingProduct = orderSummary.find(item => item.Strain === product["Strain"]);
  
    if (existingProduct) {
      // Si el producto ya está en orderSummary, solo actualizamos la cantidad
      setOrderSummary((prevSummary) =>
        prevSummary.map((item) =>
          item.Strain === product["Strain"]
            ? { ...item, quantity: item.quantity + (product.quantity || 0) } // Sumar la cantidad
            : item
        )
      );
    } else {
      // Si el producto no está, lo agregamos a orderSummary
      setOrderSummary((prevSummary) => [
        ...prevSummary,
        {
          batchID: product["Fullagro_Batch_Id"], // Use Fullagro_Batch_Id
          strain: product.Strain,
          cycle: product.Cycle,
          quantity: product.quantity || 0, // Usar la cantidad ingresada
          stock: product["US_Canada"], // Use US_Canada
        },
      ]);
    }
    setAlert({ variant: "success", title: "Producto añadido", message: "El producto se añadió a tu pedido." });
    closeProductModal(); // Cerrar el modal después de agregar
  };

  const handleQuantityChange = (event, product) => {
    const value = parseInt(event.target.value, 10);
  
    // Actualizar la cantidad en el estado
    setAllData((prevData) =>
      prevData.map((item) =>
        item === product ? { ...item, quantity: value } : item
      )
    );
  };

  const columns = useMemo(() => {
    const muiTableHeadCellPropsSettings = {
      sx: {
        fontSize: {
          xs: "10px",
          sm: "11px",
          md: "12px",
          lg: "16px",
          xl: "16px",
        },
        cursor: "help",
      },
    };

    const showOrZero = (v, suffix = "", prefix = "") => {
      if (v === null || v === undefined || v === "") return prefix + "0" + suffix;
      return prefix + v + suffix;
    };

    const toNum = (v) => {
      if (v === null || v === undefined || v === "") return 0;
      const n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    };

    return [
      {
        accessorKey: "Strain",
        header: "Strain",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Nombre comercial de la variedad",
        },
        Cell: ({ renderedCellValue }) => <strong>{renderedCellValue}</strong>,
        size: 170,
      },
      {
        accessorKey: "Fullagro_Batch_Id",
        header: "Batch ID",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Identificador interno del lote",
        },
        size: 140,
        Cell: ({ row }) => {
          const hash = row.original["Fullagro_Batch_Id"];
          return (
            <button
              className="batch-id-button"
              style={{
                background: isLight ? "#0a0e0a" : "#000",
                border: `1px solid ${isLight ? "#0a0e0a" : "#fff"}`,
                color: isLight ? "#fff" : "#fff",
                borderRadius: "16px",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "4px 10px",
              }}
              onClick={() => openProductModal(row.original)}
              title={hash ? `ID interno: ${hash}` : "Sin ID"}
            >
              <b>
                {hash && hash !== "" ? hash : "No Batch ID"}
              </b>
            </button>
          );
        },
      },
      {
        accessorKey: "Source_Percetage",
        header: "Sativity",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Porcentaje de dominancia genética (ej. 79% Indica)",
        },
        size: 100,
        sortingFn: (a, b) => toNum(a.original.Source_Percetage) - toNum(b.original.Source_Percetage),
        Cell: ({ row }) => (
          <b>{showOrZero(row.original["Source_Percetage"], "%", "(") + ")"}</b>
        ),
      },
      {
        accessorKey: "THC_Value",
        header: "THC %",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Porcentaje exacto de THC (ej. 21.5)",
        },
        size: 130,
        sortingFn: (a, b) => toNum(a.original.THC_Value) - toNum(b.original.THC_Value),
        Cell: ({ row }) => (
          <b>{showOrZero(row.original["THC_Value"], "%", "(") + ")"}</b>
        ),
      },
      {
        accessorKey: "Cycle",
        header: "Cycle",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Tipo de floración: Photo, Auto o Fast",
        },
        size: 100,
      },
      {
        accessorKey: "Flavor_Aroma_Profile",
        header: "Flavor/Aroma",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Lista de aromas de la variedad",
        },
        size: 170,
      },
      {
        accessorKey: "US_Canada",
        header: "US & Canada",
        size: 100,
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Stock disponible para US & Canada",
        },
        sortingFn: (a, b) => toNum(a.original.US_Canada) - toNum(b.original.US_Canada),
        Cell: ({ row }) => <b>{showOrZero(row.original["US_Canada"])}</b>,
      },
      {
        accessorKey: "Europe",
        header: "Europe",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Stock disponible para Europa",
        },
        size: 100,
        sortingFn: (a, b) => toNum(a.original.Europe) - toNum(b.original.Europe),
        Cell: ({ row }) => <b>{showOrZero(row.original["Europe"])}</b>,
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        muiTableHeadCellProps: {
          ...muiTableHeadCellPropsSettings,
          title: "Cantidad a agregar al pedido",
        },
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="number"
              min="0"
              value={row.original.quantity || ''}
              onChange={(e) => handleQuantityChange(e, row.original)}
              onClick={(e) => e.stopPropagation()}
              style={{ width: "80px", marginRight: "8px", color: "black" }}
            />
            <button
            style={{ width: "80px", marginRight: "8px", color: "black" }}
              onClick={(e) => {
                e.stopPropagation()
                const product = row.original;
                product.quantity && product.quantity !== '' ? handleAddToOrder(product): setAlert({ variant: "warning", title: "Falta cantidad", message: "Ingresa una cantidad mayor a 0 antes de añadir." });
              }}
            >
              Add
            </button>
          </div>
        ),
        size: 170,
      }
    ];
  }, [handleAddToOrder, isLight]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setAlert({ variant: "warning", title: "Sin sesión", message: "No hay sesión activa. Por favor, inicia sesión nuevamente." });
        return; // Return early if there's no token
      }

      // Verify Session
      const verifySessionUrl = "https://api.fullagro.com:8888/verify-session"; // Update with your URL
      try {
        const verifyResponse = await fetch(verifySessionUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!verifyResponse.ok) {
          modAuthFalse();
          throw new Error("Sesión no válida. Por favor, inicia sesión nuevamente.");
        }

        // Proceed to fetch data if session is valid
        const products = await fetch('https://api.fullagro.com:8888/products/all',{
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            "Content-Type": "application/json",
          },
        }).then( data => data.json())
        setAllData(products.products)
      } catch (error) {
        console.error("Error al verificar la sesión:", error);
         // Show error message from session verification
      }
    };

    fetchData();
  }, [modAuthFalse]);

  /* // Función para filtrar elementos únicos
  const getUniqueData = () => {
    const uniqueMap = new Map();
    allData.forEach(item => {
      uniqueMap.set(item.Strain, item); // Usar Strain como clave
    });
    
    return Array.from(uniqueMap.values());
  }; */

  return (
    <div id="dashboard">
      {confirmOrderLoader && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            zIndex: 9999,
          }}
        >
          <h2>
            <b>Creating Order.</b>
          </h2>
          <img
            src={loadingOrderGif}
            alt="Loading..."
            style={{
              width: "6rem",
              height: "6rem",
            }}
          />
        </div>
      )}
      {allData.length > 0 ? (
        <>
          <h1>Available Products</h1>
         {/*  <Button
            id="order-button"
            variant="contained"
            color="primary"
            onClick={() => orderSummary.length > 0 ? openModal(orderSummary) : setAlert({ variant: "info", title: "Carrito vacío", message: "Aún no has añadido productos al pedido." })}
          >
            <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="m5.50835165 12.5914912c-.00106615-.0057657-.00203337-.011566-.00289985-.0173991l-1.22011509-7.32069058c-.12054699-.72328196-.74633216-1.25340152-1.47959089-1.25340152h-.30574582c-.27614237 0-.5-.22385763-.5-.5s.22385763-.5.5-.5h.30574582c1.1918179 0 2.21327948.84029234 2.44951006 2h16.24474412c.3321894 0 .5720214.31795246.480762.63736056l-2 7.00000004c-.0613288.2146507-.2575218.3626394-.480762.3626394h-12.90976979l.12443308.7465985c.12054699.7232819.74633216 1.2534015 1.47959089 1.2534015h11.30574582c.2761424 0 .5.2238576.5.5s-.2238576.5-.5.5h-11.30574582c-1.22209789 0-2.26507316-.8835326-2.46598481-2.0890025l-.21991747-1.3195048zm-.08478811-6.5914912 1 6h12.69928576l1.7142857-6zm2.57643646 15c-1.1045695 0-2-.8954305-2-2s.8954305-2 2-2 2 .8954305 2 2-.8954305 2-2 2zm0-1c.55228475 0 1-.4477153 1-1s-.44771525-1-1-1-1 .4477153-1 1 .44771525 1 1 1zm9 1c-1.1045695 0-2-.8954305-2-2s.8954305-2 2-2 2 .8954305 2 2-.8954305 2-2 2zm0-1c.5522847 0 1-.4477153 1-1s-.4477153-1-1-1-1 .4477153-1 1 .4477153 1 1 1z"/></svg>
            <b>{orderSummary.length}</b>
          </Button> */}
          <MaterialReactTable
            data={allData}
            columns={columns}
            defaultColumn={{
              maxSize: 170,
              minSize: 10,              
            }}                        
            mrtTheme={(theme) => ({
              baseBackgroundColor: "#000",
            })}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => openProductModal(row.original),
              sx: {
                cursor: "pointer",
              },
            })}
            muiTableBodyProps={{
              sx: {
                "& td , *": { color: palette.text },
              },
            }}
            muiTableHeadProps={{
              sx: {
                zIndex: 1,
                "& *": { color: palette.text },
              },
            }}
            muiTopToolbarProps={{
              sx: {
                zIndex: 0,
                "& *": { color: palette.text },
                "& input": { color: palette.text },
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                zIndex: 0,
                "& *": { color: palette.text },
              },
            }}
            muiToolbarAlertBannerProps={{
              sx: {
                zIndex: 0,
                "& *": { color: palette.text, backgroundColor: palette.surface },
              },
            }}
            muiSearchTextFieldProps= {{
              placeholder: 'Search for anything',              
              variant: 'standard',
            }}         
            positionGlobalFilter="left"   
            enableDensityToggle={false}
            initialState={{
              showGlobalFilter: true,
              density: "compact",
              sorting: [
                { id: 'THC_Value', desc: true },
              ],
            }}
            
            enableStickyHeader
            enableRowVirtualization
            enableFullScreenToggle={false}
            muiShowHideColumnsButtonProps={{
              sx: { color: palette.text },
            }}
            muiColumnShowHideAllButtonProps={{
              sx: { color: palette.text },
            }}
            enableRowSelection={false}
            enableSelectAll={false}            
            enablePagination={false}
            onRowSelectionChange={setRowSelection}
            positionToolbarAlertBanner={"bottom"}            
            state={{ rowSelection }}
            
            
          />
          <ProductInfoModal
            isOpen={productModalIsOpen}
            onRequestClose={closeProductModal}
            product={selectedProduct}
            onAddToOrder={handleAddToOrder}
            productData={allData}
            onSelectBatch={handleSelectBatch}
          />
          <Modal
            isOpen={modalOrderIsOpen}
            onRequestClose={closeModal}
            style={customStyles}
            contentLabel="Order Summary"
          >
            <h2 className="fa-modal-title">Order Summary</h2>
            {orderSummary && orderSummary.length > 0 ? (
              <table className="fa-modal-table">
                <thead>
                  <tr>
                    <th>Strain</th>
                    <th>Batch ID</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {orderSummary.map((item, index) => (
                    <tr key={index}>
                      <td>{item.strain}</td>
                      <td>{item.batchID}</td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="fa-modal-empty">No hay productos en el pedido.</p>
            )}
            <div className="fa-modal-actions">
              <button className="btn secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button
                className="btn"
                onClick={() => handleConfirmOrder()}
                disabled={!orderSummary || orderSummary.length === 0}
              >
                Confirmar pedido
              </button>
            </div>
          </Modal>
        </>
      ) : (
        <div>
          <p style={{ color: palette.text, textAlign: "center" }}>
            <b>Loading Products...</b>
          </p>
          <img
            src={loadingGif}
            alt="Cargando..."
            style={{
              margin: "auto",
              display: "block",
              width: "4rem",
              height: "4rem",
            }}
          />
        </div>
      )}
      <AlertModal
        open={!!alert}
        variant={alert?.variant}
        title={alert?.title}
        message={alert?.message}
        onClose={() => setAlert(null)}
      />
    </div>
  );
}

export default Dashboard;